import React, { useState, useEffect, useRef, useMemo } from 'react';
import LinkBlock from './common/LinkBlock';
import SocialLinksBubbles from './common/SocialLinksBubbles';
import LiveChessGame from './common/LiveChessGame';
import ChessPlatformBubbleTab from './common/ChessPlatformBubbleTab';
import ImageCarousel from './common/ImageCarousel';
import YouTubeCarousel from './common/YouTubeCarousel';
import LichessStudyCarousel from './common/LichessStudyCarousel';
import { Chess } from 'chess.js';
import { doc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { fetchChessData } from '../services/chessApiService';
import { getPublishedBlogsByProfileId } from '../services/firestoreService';
import BlogSubscribeButton, { BlogSectionTitle } from './BlogSubscribeButton';
import ContactFormSection from './ContactFormSection';
import ShopPreviewSection from './ShopPreviewSection';

const DEFAULT_ACCENT = '#749555';

// Blog Section Component - extracted to use hooks properly
const BlogSection = ({ profileId, username, accentColor = DEFAULT_ACCENT, onBlogsLoaded, animate = false, isOwner = false }) => {
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isMobile, setIsMobile] = useState(false);

  // Detect mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    // Check on mount
    checkMobile();

    // Listen for resize
    window.addEventListener('resize', checkMobile);

    // Cleanup
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    const loadBlogs = async () => {
      try {
        const fetchedBlogs = await getPublishedBlogsByProfileId(profileId);

        // Sort blogs: pinned first, then by publishedAt (falls back to createdAt), newest first.
        const sortedBlogs = fetchedBlogs.sort((a, b) => {
          if (a.pinned && !b.pinned) return -1;
          if (!a.pinned && b.pinned) return 1;

          const aTime = (a.publishedAt?.seconds ?? a.createdAt?.seconds) || 0;
          const bTime = (b.publishedAt?.seconds ?? b.createdAt?.seconds) || 0;
          return bTime - aTime;
        });

        setBlogs(sortedBlogs);

        // Notify parent component about blog count
        if (onBlogsLoaded) {
          onBlogsLoaded(sortedBlogs.length > 0);
        }
      } catch (error) {
        console.error('Error loading blogs:', error);
        if (onBlogsLoaded) {
          onBlogsLoaded(false);
        }
      } finally {
        setLoading(false);
      }
    };

    if (profileId) {
      loadBlogs();
    }
  }, [profileId, onBlogsLoaded]);

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp.seconds * 1000);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const handleBlogClick = (blog) => {
    // Open in new tab using SEO-friendly URL
    window.open(`/blog/${blog.authorUsername}/${blog.slug}`, '_blank');
  };

  const handleSeeAllBlogs = () => {
    if (username) {
      window.open(`/blogs/author/${username}`, '_blank');
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center space-y-6 text-center py-8">
        <div className="relative">
          <div className="w-16 h-16 rounded-full bg-white/5 flex items-center justify-center animate-pulse">
            <i className="fas fa-chess-king text-white/30 text-3xl"></i>
          </div>
          <div className="absolute inset-0 flex items-center justify-center">
            <i className="fas fa-chess-king text-white/50 text-3xl animate-bounce"></i>
          </div>
        </div>
      </div>
    );
  }

  if (blogs.length === 0) {
    return null; // Don't show anything if no blogs
  }

  // Show max 3 blogs in preview mode
  const displayedBlogs = blogs.slice(0, 3);
  const hasMoreBlogs = blogs.length >= 4;

  return (
    <div className="space-y-4">
      {displayedBlogs.map((blog) => (
        <div
          key={blog.id}
          onClick={() => handleBlogClick(blog)}
          className="rounded-lg shadow-md hover:shadow-xl transition-all cursor-pointer overflow-hidden border-2 w-full max-w-2xl mx-auto"
          style={{ borderColor: accentColor }}
        >
          <div className="flex flex-row min-h-[180px]">
            {/* Image Section - Always show */}
            <div className={`${isMobile ? 'w-36' : 'w-56'} flex-shrink-0 flex flex-col bg-gray-900`}>
              <div className="aspect-video overflow-hidden">
                <img
                  src={blog.imageUrl || '/Images/Blog.png'}
                  alt={blog.title}
                  className="w-full h-full object-cover bg-gray-900"
                  loading="lazy"
                />
              </div>
              <div className="flex-1 flex items-center justify-center bg-gray-800 py-3 border-t border-gray-700">
                <div className={`flex items-center text-blue-400 font-semibold ${isMobile ? 'text-xs' : 'text-sm'}`}>
                  <span>Read More</span>
                  <i className="fas fa-arrow-right ml-2"></i>
                </div>
              </div>
            </div>

            {/* Content */}
            <div className={`flex-1 ${isMobile ? 'p-3' : 'p-6'} flex flex-col justify-center`}>
              <h3 className={`${isMobile ? 'text-base' : 'text-xl'} font-bold text-white mb-2 hover:opacity-90 transition-colors line-clamp-2`}>
                {blog.title}
              </h3>
              <p className="text-gray-300 text-xs mb-3">
                {formatDate(blog.publishedAt || blog.createdAt)}
              </p>
              <p className={`text-gray-200 ${isMobile ? 'text-xs' : 'text-sm'} line-clamp-3`}>
                {blog.excerpt}
              </p>
            </div>
          </div>
        </div>
      ))}

      {/* Subscribe and See All Blogs Section - Vertical Stack */}
      <div className="flex flex-col items-center gap-6 pt-6 w-full mb-8">
        {/* Subscribe Form - Always show at top */}
        <div className="w-full max-w-2xl mx-auto">
          <BlogSubscribeButton
            profileId={profileId}
            username={username}
            source="blog-preview"
            variant="compact"
            accentColor={accentColor}
            animate={animate}
            isOwner={isOwner}
          />
        </div>

        {/* See All Blogs Button - Only show if 4+ blogs */}
        {hasMoreBlogs && (
          <button
            onClick={handleSeeAllBlogs}
            className="px-8 py-3 border-2 rounded-[10px] font-semibold transition-all shadow-lg text-white hover:scale-105 mb-4"
            style={{
              borderColor: accentColor,
              backgroundColor: 'rgba(116, 149, 85, 0.2)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = accentColor;
              e.currentTarget.style.color = 'white';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'rgba(116, 149, 85, 0.2)';
              e.currentTarget.style.color = 'white';
            }}
          >
            <i className="fas fa-newspaper mr-2"></i>
            See All @{username} Blogs
          </button>
        )}
      </div>
    </div>
  );
};

// Photo Gallery Section Component - extracted to render without LinkBlock
const PhotoGallerySection = ({ profileData, accentColor }) => {
  // Handle both new structure and backward compatibility with old media array
  const photos = profileData.photos || [];
  const legacyMedia = profileData.media || [];
  const legacyPhotos = legacyMedia.filter(item => item.type === 'uploaded_image' || item.type === 'image');
  const allPhotos = photos.length > 0 ? photos : legacyPhotos;

  if (allPhotos.length === 0) {
    return null; // Don't show section if no photos
  }

  return (
    <div className="space-y-4 "  >
      <ImageCarousel
        images={allPhotos}
        className="max-w-2xl mx-auto"
        showDots={true}
        showArrows={true}
        autoHeight={true}
        accentColor={accentColor}
      />
    </div>
  );
};

// YouTube Gallery Section Component - extracted to render without LinkBlock
const YouTubeGallerySection = ({ profileData, accentColor }) => {
  // Handle both new structure and backward compatibility with old media array
  const youtubeVideos = profileData.youtubeVideos || [];
  const legacyMedia = profileData.media || [];
  const legacyVideos = legacyMedia.filter(item => item.type === 'video');
  const allVideos = youtubeVideos.length > 0 ? youtubeVideos : legacyVideos;

  if (allVideos.length === 0) {
    return null; // Don't show section if no videos
  }

  return (
    <div className="space-y-4">
      <YouTubeCarousel
        videos={allVideos}
        className="max-w-2xl mx-auto"
        showDots={true}
        showArrows={true}
        accentColor={accentColor}
      />
    </div>
  );
};

// Lichess Studies Section Component - extracted to render without LinkBlock
const LichessStudiesSection = ({ profileData, accentColor }) => {
  const lichessStudies = profileData.lichessStudies || [];

  if (lichessStudies.length === 0) {
    return null; // Don't show section if no studies
  }

  return (
    <div className="w-full">
      <LichessStudyCarousel
        studies={lichessStudies}
        className=""
        showDots={true}
        showArrows={true}
        accentColor={accentColor}
        layout={profileData?.theme?.layout}
        boardTheme={profileData?.lichessBoardTheme || 'brown'}
      />
    </div>
  );
};

const clampChannel = (value) => Math.min(Math.max(value, 0), 255);

const normalizeHex = (hex) => {
  if (!hex) return null;
  let normalized = hex.replace('#', '');
  if (normalized.length === 3) {
    normalized = normalized.split('').map(char => char + char).join('');
  }
  if (normalized.length !== 6) {
    return null;
  }
  return normalized;
};

const adjustColor = (hex, amount = 0) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return hex || DEFAULT_ACCENT;
  const num = parseInt(normalized, 16);
  let r = (num >> 16) & 255;
  let g = (num >> 8) & 255;
  let b = num & 255;

  r = clampChannel(r + Math.round(255 * amount));
  g = clampChannel(g + Math.round(255 * amount));
  b = clampChannel(b + Math.round(255 * amount));

  return `#${[r, g, b]
    .map(channel => channel.toString(16).padStart(2, '0'))
    .join('')}`;
};

const hexToRgba = (hex, alpha = 1) => {
  const normalized = normalizeHex(hex);
  if (!normalized) return `rgba(116, 149, 85, ${alpha})`;
  const num = parseInt(normalized, 16);
  const r = (num >> 16) & 255;
  const g = (num >> 8) & 255;
  const b = num & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

// Secret utility function to parse (*text*) into clickable links
const parseLinksFromText = (text, accentColor = DEFAULT_ACCENT) => {
  if (!text) return text;
  
  const linkPattern = /\(\*([^*]+)\*\)/g;
  const parts = [];
  let lastIndex = 0;
  let match;
  
  while ((match = linkPattern.exec(text)) !== null) {
    // Add text before the match
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    
    const linkContent = match[1];
    let linkText = linkContent;
    let linkUrl = linkContent;
    
    // Check for custom text formats: "text: url" or "text | url"
    const colonSplit = linkContent.split(': ');
    const pipeSplit = linkContent.split(' | ');
    
    if (colonSplit.length === 2) {
      linkText = colonSplit[0].trim();
      linkUrl = colonSplit[1].trim();
    } else if (pipeSplit.length === 2) {
      linkText = pipeSplit[0].trim();
      linkUrl = pipeSplit[1].trim();
    }
    
    // Validate URL format
    const isValidUrl = /^https?:\/\/.+/i.test(linkUrl);
    
    if (isValidUrl) {
      parts.push(
        <a
          key={match.index}
          href={linkUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium"
          style={{ color: accentColor }}
        >
          {linkText}
        </a>
      );
    } else {
      // If not a valid URL, just render the original text
      parts.push(match[0]);
    }
    
    lastIndex = match.index + match[0].length;
  }
  
  // Add remaining text after the last match
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  
  return parts.length > 1 ? parts : text;
};

// Chess Ratings Section Component
const ChessRatingsSection = ({ chessComUsername, lichessUsername, fideRating, nationalRating, federation, showRatings = false }) => {
  const [chessComData, setChessComData] = useState(null);
  const [lichessData, setLichessData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const loadRatings = async () => {
      if (!showRatings || (!chessComUsername && !lichessUsername && !fideRating && !nationalRating)) return;
      
      setLoading(true);
      
      // Fetch Chess.com data
      if (chessComUsername) {
        try {
          const data = await fetchChessData('chessCom', chessComUsername);
          setChessComData(data);
        } catch (error) {
          console.error('Error fetching Chess.com data:', error);
        }
      }
      
      // Fetch Lichess data  
      if (lichessUsername) {
        try {
          const data = await fetchChessData('lichess', lichessUsername);
          setLichessData(data);
        } catch (error) {
          console.error('Error fetching Lichess data:', error);
        }
      }
      
      setLoading(false);
    };

    loadRatings();
  }, [chessComUsername, lichessUsername, fideRating, nationalRating, showRatings]);

  // Don't render if ratings are disabled or no ratings available
  const hasAnyRating = fideRating || nationalRating || (chessComData?.rating && chessComData.rating > 0) || (lichessData?.rating && lichessData.rating > 0);
  
  if (!showRatings || (!hasAnyRating && !loading)) {
    return null;
  }

  return (
    <div className="mt-8">
      <div className="flex items-center mb-6">
        <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-yellow-600 rounded-full flex items-center justify-center mr-3 shadow-lg">
          <i className="fas fa-chess text-white text-lg"></i>
        </div>
        <h4 className="text-2xl font-bold text-white">Chess Ratings</h4>
      </div>

      {loading ? (
        <div className="bg-gradient-to-br from-blue-900/30 to-indigo-900/30 rounded-xl p-8 text-center border border-blue-500">
          <div className="w-12 h-12 bg-blue-900/50 rounded-full flex items-center justify-center mx-auto mb-4">
            <i className="fas fa-spinner fa-spin text-blue-400 text-xl"></i>
          </div>
          <p className="text-blue-300 font-medium">Loading ratings...</p>
          <p className="text-blue-400 text-sm mt-1">Fetching live data from chess platforms</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {/* FIDE Rating or Federation Rating Fallback */}
          {fideRating ? (
            <div className="bg-gray-800 rounded-xl p-3 border-2 border-gray-700 hover:border-gray-600 transition-all duration-200 hover:shadow-lg group">
              <div className="text-center mb-2">
                <div className="w-10 h-10 bg-gray-700 rounded-lg flex items-center justify-center mx-auto mb-1">
                  <i className="fas fa-globe text-gray-300 text-lg"></i>
                </div>
                <div className="font-bold text-gray-100 text-lg">FIDE</div>
                <div className="bg-gray-700 text-gray-200 px-2 py-1 rounded-full text-xs font-semibold mt-1 inline-block">
                  Official
                </div>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-gray-100 mb-0">{fideRating}</p>
                <p className="text-gray-300 text-sm font-medium">Official Rating</p>
              </div>
            </div>
          ) : nationalRating && (
            <div className="bg-gradient-to-br from-purple-900/30 to-violet-900/30 rounded-xl p-3 border-2 border-purple-500 hover:border-purple-400 transition-all duration-200 hover:shadow-lg group">
              <div className="text-center mb-2">
                <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center mx-auto mb-1">
                  <i className="fas fa-globe text-purple-400 text-lg"></i>
                </div>
                <div className="font-bold text-gray-100 text-lg">{federation || 'Federation'}</div>
                <div className="bg-gray-800 text-gray-200 px-2 py-1 rounded-full text-xs font-semibold mt-1 inline-block">
                  National
                </div>
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-purple-400 mb-0">{nationalRating}</p>
                <p className="text-purple-300 text-sm font-medium">National Rating</p>
              </div>
            </div>
          )}
          
          {/* Chess.com Rating */}
          {chessComData?.rating && chessComData.rating > 0 && (
            <div className="bg-gradient-to-br from-chess-green to-green-600 rounded-xl p-3 border-2 border-chess-green hover:border-green-600 transition-all duration-200 hover:shadow-lg group">
              <div className="text-center mb-2">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-1">
                  <img src="/Images/Chesscom.png" alt="Chess.com" className="w-10 h-10 rounded-lg" loading="lazy" />
                </div>
                <div className="font-bold text-white text-lg">Chess.com</div>
                {chessComData.ratingType && (
                  <div className="bg-white text-gray-800 px-2 py-1 rounded-full text-xs font-semibold mt-1 inline-block">
                    {chessComData.ratingType}
                      </div>
                )}
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white mb-0">{chessComData.rating}</p>
                <p className="text-white text-opacity-90 text-sm font-medium">Current Rating</p>
              </div>
            </div>
          )}
          
          {/* Lichess Rating */}
          {lichessData?.rating && lichessData.rating > 0 && (
            <div className="bg-gradient-to-br from-gray-800 to-black rounded-xl p-3 border-2 border-gray-700 hover:border-gray-600 transition-all duration-200 hover:shadow-lg group">
              <div className="text-center mb-2">
                <div className="w-10 h-10 flex items-center justify-center mx-auto mb-1">
                  <img src="/Images/LiChess.png" alt="Lichess" className="w-10 h-10 rounded-lg" loading="lazy" />
                </div>
                <div className="font-bold text-white text-lg">Lichess.org</div>
                {lichessData.ratingType && (
                  <div className="bg-white text-gray-800 px-2 py-1 rounded-full text-xs font-semibold mt-1 inline-block">
                    {lichessData.ratingType}
                  </div>
                )}
              </div>
              <div className="text-center">
                <p className="text-3xl font-bold text-white mb-0">{lichessData.rating}</p>
                <p className="text-white text-opacity-80 text-sm font-medium">Current Rating</p>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const PlayerPreviewSection = ({ profileData, toggles, activeTab, setActiveTab, isPublicView = false, profileId }) => {
  const { currentUser } = useAuth();
  
  const titleFullNames = {
    'GM': 'Grandmaster',
    'IM': 'International Master',
    'FM': 'FIDE Master',
    'CM': 'Candidate Master',
    'WGM': 'Woman Grandmaster',
    'WIM': 'Woman International Master',
    'WFM': 'Woman FIDE Master',
    'WCM': 'Woman Candidate Master'
  };

  // Profile type configuration
  const profileTypeConfig = {
    coach: { label: 'Coach', icon: 'fa-chalkboard-teacher' },
    creator: { label: 'Creator', icon: 'fa-video' },
    videoEditor: { label: 'Editor', icon: 'fa-film' },
    photographer: { label: 'Photographer', icon: 'fa-camera' },
    streamer: { label: 'Streamer', icon: 'fa-broadcast-tower' },
    organizer: { label: 'Tournament Organizer', icon: 'fa-trophy' },
    arbiter: { label: 'Arbiter', icon: 'fa-gavel' },
    commentator: { label: 'Commentator', icon: 'fa-microphone' },
    analyst: { label: 'Analyst', icon: 'fa-chart-line' },
    writer: { label: 'Journalist', icon: 'fa-pen' },
    puzzleCreator: { label: 'Puzzler', icon: 'fa-puzzle-piece' },
    openingTheorist: { label: 'Openings', icon: 'fa-chess' },
    endgameSpecialist: { label: 'Endgames', icon: 'fa-chess-king' },
    tactician: { label: 'Tactician', icon: 'fa-chess-knight' }
  };

  const profileType = profileData?.theme?.profileType || '';
  
  // Ensure backward compatibility with existing profiles and new split structure
  const enhancedToggles = useMemo(() => ({
    ...toggles,
    // Backward compatibility: if old showMedia exists, map to new structure
    showPhotos: toggles.showPhotos !== undefined ? toggles.showPhotos : (toggles.showMedia !== undefined ? toggles.showMedia : true),
    showYouTube: toggles.showYouTube !== undefined ? toggles.showYouTube : (toggles.showMedia !== undefined ? toggles.showMedia : true),
    showLichessStudies: toggles.showLichessStudies !== undefined ? toggles.showLichessStudies : false
  }), [toggles]);

  const [expandedBlock, setExpandedBlock] = useState(null);
  const [copyFeedback, setCopyFeedback] = useState('');
  const [activeChessPlatform, setActiveChessPlatform] = useState(null);
  const [expandedTitle, setExpandedTitle] = useState(false);
  const [isUserTurn, setIsUserTurn] = useState(false);
  const [chessGamePosition, setChessGamePosition] = useState(null);
  const [showTransitionState, setShowTransitionState] = useState(false);
  const [previousUserTurn, setPreviousUserTurn] = useState(false);
  const chessGameRef = useRef(null);
  const hasAutoOpenedRef = useRef(false);
  const userHasManuallyClosedChessRef = useRef(false);
  const [hasBlogsToSubscribe, setHasBlogsToSubscribe] = useState(false);
  const blogSubscribeRef = useRef(null);
  const [justClickedSubscribe, setJustClickedSubscribe] = useState(false);
  const [showSubscribeButtonAnimation, setShowSubscribeButtonAnimation] = useState(false);
  const sectionRefs = useRef({});

  const primaryColor = profileData?.theme?.primaryColor || DEFAULT_ACCENT;
  const primaryColorDark = adjustColor(primaryColor, -0.15);
  const primaryColorSoft = hexToRgba(primaryColor, 0.12);
  const isOwner = Boolean(currentUser?.uid && (profileData?.userId === currentUser.uid || profileData?.ownerId === currentUser.uid));
  const isChessGameExpanded = expandedBlock === 'chessGame';
  const chessGameHeaderStyle = isChessGameExpanded
    ? {
        backgroundColor: primaryColor,
        borderColor: hexToRgba(primaryColor, 0.5)
      }
    : (isUserTurn || showTransitionState)
      ? {}
      : { borderColor: hexToRgba(primaryColor, 0.35) };
  const chessGameIconWrapperStyle = isChessGameExpanded
    ? { backgroundColor: 'rgba(255, 255, 255, 0.25)' }
    : (isUserTurn || showTransitionState)
      ? {}
      : { backgroundColor: primaryColorSoft };
  const chessGameIconStyle = isChessGameExpanded
    ? { color: '#ffffff' }
    : isUserTurn
      ? { color: '#dc2626' }
      : showTransitionState
        ? { color: '#16a34a' }
        : { color: primaryColor };
  const chessGameTitleStyle = isChessGameExpanded
    ? { color: '#ffffff' }
    : isUserTurn
      ? { color: '#ef4444' }
      : showTransitionState
        ? { color: '#22c55e' }
        : { color: '#ffffff' };
  const chessGameChevronStyle = isChessGameExpanded
    ? { color: '#ffffff' }
    : isUserTurn
      ? { color: '#dc2626' }
      : showTransitionState
        ? { color: '#16a34a' }
        : { color: primaryColor };


  // Monitor chess game for user's turn
  useEffect(() => {
    const actualProfileId = profileId || profileData.id;
    
    if (!actualProfileId || !profileData.chessGame?.enabled || !enhancedToggles.showChessGame) {
      setIsUserTurn(false);
      return;
    }

    const gameDocRef = doc(db, 'playerProfiles', actualProfileId, 'chessGames', 'current');
    
    const unsubscribe = onSnapshot(gameDocRef, (gameDoc) => {
      try {
        if (gameDoc.exists()) {
          const gameData = gameDoc.data();
          if (gameData.pgn) {
            const chess = new Chess();
            chess.loadPgn(gameData.pgn);
            const currentTurn = chess.turn(); // 'w' for white, 'b' for black
            setChessGamePosition(chess.fen());
            
            // Determine if it's the current user's turn
            const isProfileOwner = (currentUser?.uid === profileData.userId) || 
                                  (actualProfileId && localStorage.getItem('ownerAuth_' + actualProfileId) === 'true');
            
            const ownerColor = profileData.chessGame?.ownerColor || 'white';
            const userColor = isProfileOwner ? ownerColor : (ownerColor === 'white' ? 'black' : 'white');
            const userColorCode = userColor === 'white' ? 'w' : 'b';
            
            // Check if it's the user's turn and game is not over
            const isGameOver = chess.isGameOver();
            const isUsersTurn = currentTurn === userColorCode && !isGameOver;
            
            // Update the current turn state
            setIsUserTurn(isUsersTurn);
            setChessGamePosition(chess.fen());
          } else {
            // New game - check if user should start
            const isProfileOwner = (currentUser?.uid === profileData.userId) || 
                                  (actualProfileId && localStorage.getItem('ownerAuth_' + actualProfileId) === 'true');
            const ownerColor = profileData.chessGame?.ownerColor || 'white';
            const userColor = isProfileOwner ? ownerColor : (ownerColor === 'white' ? 'black' : 'white');
            
            // New game - white starts first
            setIsUserTurn(userColor === 'white');
          }
        } else {
          // No game exists yet - check if user should start
          const isProfileOwner = currentUser?.uid === profileData.userId || 
                                (actualProfileId && localStorage.getItem('ownerAuth_' + actualProfileId) === 'true');
          const ownerColor = profileData.chessGame?.ownerColor || 'white';
          const userColor = isProfileOwner ? ownerColor : (ownerColor === 'white' ? 'black' : 'white');
          
          // New game - white starts first
          setIsUserTurn(userColor === 'white');
        }
      } catch (error) {
        console.error('Error checking chess turn:', error);
        setIsUserTurn(false);
      }
    });

    return () => unsubscribe();
  }, [profileId, profileData.id, profileData.chessGame?.enabled, profileData.chessGame?.ownerColor, profileData.userId, enhancedToggles.showChessGame, currentUser?.uid]);

  // Handle transition when user turn changes
  useEffect(() => {
    // Check if user just stopped being their turn (made a move)
    if (previousUserTurn && !isUserTurn) {
      // User just made a move - show transition state
      setShowTransitionState(true);
      
      setTimeout(() => {
        // After 1.5s, hide transition state and scroll to new position
        setShowTransitionState(false);
        
        // Small delay to let the section render in new position, then scroll
        setTimeout(() => {
          if (chessGameRef.current) {
            const elementRect = chessGameRef.current.getBoundingClientRect();
            const absoluteElementTop = elementRect.top + window.pageYOffset;
            const targetPosition = absoluteElementTop - 100; // 100px above the element
            
            window.scrollTo({
              top: targetPosition,
              behavior: 'smooth'
            });
          }
        }, 100); // Small delay for DOM update
      }, 1500);
    }
    
    // Update previous turn for next comparison
    setPreviousUserTurn(isUserTurn);
  }, [isUserTurn, previousUserTurn]);

  // Trigger Subscribe button animation 1.5s after the button becomes visible
  useEffect(() => {
    if (hasBlogsToSubscribe && enhancedToggles.showBlog) {
      const timer = setTimeout(() => {
        setShowSubscribeButtonAnimation(true);
        // Stop animation after it completes (1.5s duration)
        setTimeout(() => {
          setShowSubscribeButtonAnimation(false);
        }, 1500);
      }, 1500);

      return () => clearTimeout(timer);
    }
  }, [hasBlogsToSubscribe, enhancedToggles.showBlog]);

  // Available sections based on toggles
  const sections = [
    { id: 'basicInfo', label: 'About Me', icon: 'fa-user', show: enhancedToggles.showBasicInfo },
    { id: 'chessDetails', label: 'Chess Ratings', icon: 'fa-chess', show: enhancedToggles.showChessDetails },
    { id: 'achievements', label: 'Achievements', icon: 'fa-trophy', show: enhancedToggles.showAchievements },
    { id: 'coaching', label: 'Coaching', icon: 'fa-chalkboard-teacher', show: enhancedToggles.showCoaching },
    { id: 'photos', label: 'Photo Gallery', icon: 'fa-camera', show: enhancedToggles.showPhotos || enhancedToggles.showMedia }, // backward compatibility
    { id: 'youtube', label: 'YouTube Gallery', icon: 'fa-circle-play', show: enhancedToggles.showYouTube },
    { id: 'lichessStudies', label: 'Games & Studies', icon: 'fa-chess-board', show: enhancedToggles.showLichessStudies },
    { id: 'goals', label: 'Fun & Goals', icon: 'fa-flag-checkered', show: enhancedToggles.showGoals },
    { id: 'chessGame', label: profileData.chessGame?.title || 'Play Chess', icon: 'fa-chess-board', show: enhancedToggles.showChessGame },
    { id: 'affiliateLinks', label: 'Affiliate Links', icon: 'fa-link', show: (() => {
      const activeLinks = profileData.affiliateLinks?.filter(link => link.isActive) || [];
      return enhancedToggles.showAffiliateLinks && activeLinks.length > 0;
    })() },
    { id: 'referral', label: profileData.referral?.ctaText || 'Create Your Own Page', icon: 'fa-rocket', show: enhancedToggles.showReferral && profileData.referral?.enabled },
    { id: 'blog', label: 'Blog', icon: 'fa-newspaper', show: enhancedToggles.showBlog },
    { id: 'shop', label: 'Shop', icon: 'fa-shopping-cart', show: profileData.playerPremium && enhancedToggles.showShop },
    { id: 'contact', label: 'Contact Me', icon: 'fa-envelope', show: profileData.playerPremium && enhancedToggles.showMessages },
  ];

  // Add custom sections
  const customSections = profileData.customSections || {};
  const customSectionsList = Object.keys(customSections).map(sectionId => {
    const section = customSections[sectionId];
    // Use custom icon if available, otherwise fallback to type-based icons
    const icon = section?.icon || (section?.type === 'link' ? 'fa-link' : 'fa-info-circle');
    return {
      id: `custom_${sectionId}`,
      label: section?.title || `Custom ${sectionId.slice(-1)}`,
      icon: icon,
      show: enhancedToggles[`showCustom${sectionId}`] !== false,
      isCustom: true,
      sectionId,
      data: section
    };
  });

  const allSections = [...sections, ...customSectionsList];

  // Sort sections based on saved order or default order
  const getSortedSections = () => {
    // Filter sections: hide chess game unless it's user's turn
    const visibleSections = allSections.filter(section => {
      if (section.id === 'chessGame') {
        return section.show && isUserTurn;
      }
      return section.show;
    });
    const sectionOrder = profileData.sectionOrder;

    if (!sectionOrder || sectionOrder.length === 0) {
      // Default order: chess game first, then custom sections, then standard sections
      return visibleSections.sort((a, b) => {
        if (a.id === 'chessGame') return -1;
        if (b.id === 'chessGame') return 1;
        if (a.isCustom && !b.isCustom) return -1;
        if (!a.isCustom && b.isCustom) return 1;
        return 0;
      });
    }

    // Use custom order, but include any sections not in the order
    const orderedSections = [];
    const sectionsById = {};
    
    visibleSections.forEach(section => {
      sectionsById[section.id] = section;
    });

    // Add sections in specified order
    sectionOrder.forEach(sectionId => {
      if (sectionsById[sectionId]) {
        orderedSections.push(sectionsById[sectionId]);
        delete sectionsById[sectionId];
      }
    });

    // Add any remaining sections (new sections not in order yet)
    Object.values(sectionsById).forEach(section => {
      if (section.isCustom) {
        // Insert custom sections at beginning
        orderedSections.unshift(section);
      } else if (section.id === 'chessGame') {
        // Insert chess game at beginning
        orderedSections.unshift(section);
      } else {
        // Insert other standard sections at end
        orderedSections.push(section);
      }
    });

    return orderedSections;
  };

  const visibleSections = useMemo(() => getSortedSections(), [
    profileData.playerPremium,
    profileData.customSections,
    profileData.sectionOrder,
    profileData.chessGame,
    profileData.referral,
    enhancedToggles.showBasicInfo,
    enhancedToggles.showChessDetails,
    enhancedToggles.showAchievements,
    enhancedToggles.showCoaching,
    enhancedToggles.showPhotos,
    enhancedToggles.showMedia,
    enhancedToggles.showYouTube,
    enhancedToggles.showGoals,
    enhancedToggles.showChessGame,
    enhancedToggles.showReferral,
    enhancedToggles.showBlog,
    allSections.length,
    isUserTurn
  ]);

  // Auto-open the first section in the ordered list (unless chess game is priority)
  // DISABLED: No longer auto-opening sections on page load
  /*
  useEffect(() => {
    // Only auto-open once on initial mount
    if (hasAutoOpenedRef.current) {
      // After initial open, only change if it's user's turn in chess game AND user hasn't manually closed it
      if (isUserTurn && visibleSections.some(section => section.id === 'chessGame') && !userHasManuallyClosedChessRef.current) {
        setExpandedBlock('chessGame');
      }
      return;
    }

    if (visibleSections.length > 0) {
      // If it's user's turn in chess and chess game is visible, prioritize that
      if (isUserTurn && visibleSections.some(section => section.id === 'chessGame')) {
        setExpandedBlock('chessGame');
      } else {
        // Otherwise, open the first section in the ordered list
        setExpandedBlock(visibleSections[0].id);
      }
      hasAutoOpenedRef.current = true;
    }
  }, [visibleSections, isUserTurn]);
  */

  // Detect top section for special highlighting
  const topSectionId = (() => {
    if (visibleSections.length === 0) return null;
    return visibleSections[0].id;
  })();

  const handleBlockToggle = (sectionId) => {
    // Track if user manually closes chess game
    if (sectionId === 'chessGame' && expandedBlock === 'chessGame') {
      userHasManuallyClosedChessRef.current = true;
    }

    const isExpanding = expandedBlock !== sectionId;
    setExpandedBlock(expandedBlock === sectionId ? null : sectionId);

    // Scroll to section top when expanding, with comfortable margin
    if (isExpanding && sectionRefs.current[sectionId]) {
      setTimeout(() => {
        const element = sectionRefs.current[sectionId];
        if (element) {
          const elementRect = element.getBoundingClientRect();
          const absoluteElementTop = elementRect.top + window.pageYOffset;
          const targetPosition = absoluteElementTop - 10; // 10px margin above the section

          window.scrollTo({
            top: targetPosition,
            behavior: 'smooth'
          });
        }
      }, 350);
    }

    // Close chess platform bubble when opening a regular section
    setActiveChessPlatform(null);
  };

  const handleChessPlatformClick = (platform) => {
    setActiveChessPlatform(platform);
    // Close any open expandable blocks
    setExpandedBlock(null);
  };

  const handleCopyLink = async () => {
    const currentUrl = window.location.href;
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopyFeedback('Link copied!');
      setTimeout(() => setCopyFeedback(''), 2000);
    } catch (err) {
      setCopyFeedback('Failed to copy');
      setTimeout(() => setCopyFeedback(''), 2000);
    }
  };

  const handleScrollToSubscribe = () => {
    if (blogSubscribeRef.current) {
      setJustClickedSubscribe(true);
      blogSubscribeRef.current.scrollIntoView({ behavior: 'smooth', block: 'end' });

      // Clear animation flag after animation completes
      setTimeout(() => {
        setJustClickedSubscribe(false);
      }, 2000);
    }
  };

  const formatUrl = (url) => {
    if (!url) return url;
    // If URL doesn't start with http:// or https://, add https://
    if (!url.match(/^https?:\/\//)) {
      return `https://${url}`;
    }
    return url;
  };


  const renderBasicInfo = () => (
    <div className="space-y-4">
      {profileData.bio && (
        <div>
          <p className="text-gray-200 whitespace-pre-wrap leading-relaxed text-xl font-medium">
            {parseLinksFromText(profileData.bio, primaryColor)}
          </p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {(profileData.location || profileData.country) && (
          <div className="flex items-center text-gray-300">
            <i className="fas fa-map-marker-alt mr-3 text-red-400"></i>
            <div>
              <span className="font-bold text-gray-100">Location:</span>
              <span className="ml-2 text-gray-200">
                {profileData.location || [profileData.city, profileData.country].filter(Boolean).join(', ')}
              </span>
            </div>
          </div>
        )}
        
{(() => {
          const primarySocial = profileData.primarySocialProfile;
          const socialLinks = profileData.socialLinks || {};
          const chessLinks = profileData.links || {};
          
          // Get the primary social data
          let primarySocialData = null;
          if (primarySocial && (socialLinks[primarySocial] || chessLinks[primarySocial])) {
            const value = socialLinks[primarySocial] || chessLinks[primarySocial];
            let url = '';
            let icon = '';
            let label = '';
            
            switch (primarySocial) {
              case 'youtube':
                url = value.startsWith('http') ? value : `https://youtube.com/@${value}`;
                icon = 'fab fa-youtube text-red-600';
                label = 'YouTube';
                break;
              case 'twitter':
                url = `https://twitter.com/${value}`;
                icon = 'fab fa-x-twitter text-black';
                label = 'Twitter/X';
                break;
              case 'instagram':
                url = `https://instagram.com/${value}`;
                icon = 'fab fa-instagram text-purple-600';
                label = 'Instagram';
                break;
              case 'tiktok':
                url = `https://tiktok.com/@${value}`;
                icon = 'fab fa-tiktok text-black';
                label = 'TikTok';
                break;
              case 'twitch':
                url = `https://twitch.tv/${value}`;
                icon = 'fab fa-twitch text-purple-500';
                label = 'Twitch';
                break;
              case 'discord':
                url = value.startsWith('http') ? value : `#`;
                icon = 'fab fa-discord text-indigo-600';
                label = 'Discord';
                break;
              case 'chessCom':
                url = `https://chess.com/member/${value}`;
                icon = 'fas fa-chess text-green-600';
                label = 'Chess.com';
                break;
              case 'lichess':
                url = `https://lichess.org/@/${value}`;
                icon = 'fas fa-chess text-green-600';
                label = 'Lichess';
                break;
              case 'fideId':
                url = `https://ratings.fide.com/profile/${value}`;
                icon = 'fas fa-chess text-green-600';
                label = 'FIDE';
                break;
            }
            
            if (url && url !== '#') {
              primarySocialData = { url, icon, label, value };
            } else if (primarySocial === 'discord') {
              primarySocialData = { url: null, icon, label, value };
            }
          }
          
          // Show primary social if available, otherwise show username
          if (primarySocialData) {
            return (
              <div className="flex items-center text-gray-300">
                <i style={{ color: primaryColor }} className={`${primarySocialData.icon} mr-3 text-xl`}></i>
                <div>
                  <span className="font-bold text-gray-10">Primary {primarySocialData.label}:</span>
                  {primarySocialData.url ? (
                    <a
                      href={primarySocialData.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 text-blue-400 hover:underline"
                    >
                      {primarySocialData.value}
                    </a>
                  ) : (
                    <span className="ml-2 text-gray-200">{primarySocialData.value}</span>
                  )}
                </div>
              </div>
            );
          } else if (profileData.username) {
            return (
              <div className="flex items-center text-gray-300">
                <i className="fas fa-user mr-3 text-purple-400"></i>
                <div>
                  <span className="font-bold text-gray-100">Username:</span>
                  <span className="ml-2 text-gray-200">{profileData.username}</span>
                </div>
              </div>
            );
          }
          return null;
        })()}
      </div>
      
      {/* Email */}
      {profileData.email && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
          <div className="flex items-center text-gray-300">
            <i className="fas fa-envelope mr-3 text-blue-400"></i>
            <div>
              <span className="font-bold text-gray-100">Email:</span>
              <a
                href={`mailto:${profileData.email}`}
                className="ml-2 text-blue-400 hover:underline"
              >
                {profileData.email}
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Chess Ratings */}
      <ChessRatingsSection 
        chessComUsername={profileData.links?.chessCom}
        lichessUsername={profileData.links?.lichess}
        fideRating={profileData.fideRating}
        nationalRating={profileData.nationalRating}
        federation={profileData.federation}
        showRatings={enhancedToggles.showChessRatings}
      />
    </div>
  );

  const renderChessDetails = () => (
    <div className="space-y-6">
      {/* Ratings and IDs */}
      {(profileData.fideId || profileData.federation || profileData.federationId || profileData.fideRating || profileData.nationalRating) && (
        <div>
          <div className="grid grid-cols-2 gap-4">
            {profileData.fideId && (
              <div>
                <label className="block text-sm font-medium text-gray-400">FIDE ID</label>
                <p className="mt-1 text-lg text-gray-100">{profileData.fideId}</p>
              </div>
            )}
            {profileData.federation && (
              <div>
                <label className="block text-sm font-medium text-gray-400">Federation</label>
                <p className="mt-1 text-lg text-gray-100">{profileData.federation}</p>
              </div>
            )}
            {profileData.federationId && (
              <div>
                <label className="block text-sm font-medium text-gray-400">Federation ID</label>
                <p className="mt-1 text-lg text-gray-100">{profileData.federationId}</p>
              </div>
            )}
            {profileData.fideRating && (
              <div>
                <label className="block text-sm font-medium text-gray-400">FIDE Rating</label>
                <p className="mt-1 text-2xl font-bold text-blue-400">{profileData.fideRating}</p>
              </div>
            )}
            {profileData.nationalRating && (
              <div>
                <label className="block text-sm font-medium text-gray-400">National Rating</label>
                <p className="mt-1 text-2xl font-bold text-green-400">{profileData.nationalRating}</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderAchievements = () => {
    const achievements = profileData.achievements || [];

    if (achievements.length === 0) {
      return (
        <div className="text-center py-8">
          <i className="fas fa-trophy text-4xl text-gray-400 mb-4"></i>
          <p className="text-gray-300">No achievements listed yet.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="space-y-4">
          {achievements.map((achievement, index) => (
            <div key={achievement.id || index} className="relative">
              {/* Timeline line */}
              {index < achievements.length - 1 && (
                <div className="absolute left-6 top-12 w-0.5 h-8 bg-gray-300"></div>
              )}
              
              <div className="flex items-start space-x-4">
                {/* Year badge */}
                <div
                  className="text-white px-4 py-2 rounded-full text-sm font-bold shadow-lg min-w-20 text-center flex-shrink-0"
                  style={{
                    backgroundColor: primaryColor,
                    boxShadow: `0 8px 20px ${hexToRgba(primaryColor, 0.35)}`
                  }}
                >
                  {achievement.year}
                </div>
                
                {/* Achievement card */}
                <div className="flex-1 bg-gray-800 border border-gray-700 rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-100 mb-1">
                        {achievement.achievement || achievement.title || 'Achievement'}
                      </h4>
                      {achievement.event && achievement.title && (
                        <p className="text-sm mb-2" style={{ color: primaryColor }}>
                          <i className="fas fa-calendar-alt mr-1"></i>
                          {achievement.event}
                        </p>
                      )}
                      {achievement.description && (
                        <p className="text-gray-300 text-sm leading-relaxed">
                          {achievement.description}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderCoaching = () => {
    const coaching = profileData.coaching || {};

    if (!coaching.available) {
      return (
        <div className="text-center py-8">
          <i className="fas fa-chalkboard-teacher text-4xl text-gray-400 mb-4"></i>
          <p className="text-gray-300">Coaching services not available.</p>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        {coaching.description && (
          <div>
            <h4 className="font-bold text-white mb-2">About My Coaching</h4>
            <p className="text-gray-200 whitespace-pre-wrap">{parseLinksFromText(coaching.description, primaryColor)}</p>
          </div>
        )}

        {coaching.rates && (
          <div>
            <h4 className="font-bold text-white mb-2">Rates & Pricing</h4>
            <p className="text-gray-200 whitespace-pre-wrap">{coaching.rates}</p>
          </div>
        )}

        {coaching.specializations && coaching.specializations.length > 0 && (
          <div>
            <h4 className="font-bold text-white mb-3">Specializations</h4>
            <div className="flex flex-wrap gap-2">
              {coaching.specializations.map((specialization, index) => (
                <span
                  key={index}
                  className="px-3 py-1 text-sm rounded-full font-medium text-white"
                  style={{
                    backgroundColor: hexToRgba(primaryColor, 0.3),
                    border: `1px solid ${hexToRgba(primaryColor, 0.6)}`
                  }}
                >
                  {specialization}
                </span>
              ))}
            </div>
          </div>
        )}

        {coaching.email && (
          <div className="pt-4">
            <a 
              href={`mailto:${coaching.email}`}
              className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors hover:opacity-95"
              style={{
                backgroundColor: primaryColor,
                boxShadow: `0 8px 20px ${hexToRgba(primaryColor, 0.3)}`
              }}
            >
              <i className="fas fa-envelope mr-2"></i>
              Contact for Coaching
            </a>
          </div>
        )}
      </div>
    );
  };

  const renderPhotos = () => {
    // Handle both new structure and backward compatibility with old media array
    const photos = profileData.photos || [];
    const legacyMedia = profileData.media || [];
    const legacyPhotos = legacyMedia.filter(item => item.type === 'uploaded_image' || item.type === 'image');
    const allPhotos = photos.length > 0 ? photos : legacyPhotos;

    if (allPhotos.length === 0) {
      return (
        <div className="text-center py-8">
          <i className="fas fa-camera text-4xl text-gray-400 mb-4"></i>
          <p className="text-gray-300">No photos uploaded yet.</p>
        </div>
      );
    }

    return (
      <ImageCarousel
        images={allPhotos}
        className="max-w-2xl mx-auto"
        showDots={true}
        showArrows={true}
        autoHeight={true}
        accentColor={primaryColor}
      />
    );
  };

  const renderYouTube = () => {
    // Handle both new structure and backward compatibility with old media array
    const youtubeVideos = profileData.youtubeVideos || [];
    const legacyMedia = profileData.media || [];
    const legacyVideos = legacyMedia.filter(item => item.type === 'video');
    const allVideos = youtubeVideos.length > 0 ? youtubeVideos : legacyVideos;

    if (allVideos.length === 0) {
      return (
        <div className="text-center py-8">
          <i className="fab fa-youtube text-4xl text-red-400 mb-4"></i>
          <p className="text-gray-300">No YouTube videos added yet.</p>
        </div>
      );
    }

    return (
      <YouTubeCarousel
        videos={allVideos}
        className="max-w-2xl mx-auto"
        showDots={true}
        showArrows={true}
        accentColor={primaryColor}
      />
    );
  };

  const renderLichessStudies = () => {
    const lichessStudies = profileData.lichessStudies || [];

    if (lichessStudies.length === 0) {
      return (
        <div className="text-center py-8">
          <i className="fas fa-chess-board text-4xl text-gray-800 mb-4"></i>
          <p className="text-gray-300">No Lichess studies added yet.</p>
        </div>
      );
    }

    return (
      <LichessStudyCarousel
        studies={lichessStudies}
        className="max-w-2xl mx-auto"
        showDots={true}
        showArrows={true}
        accentColor={primaryColor}
        boardTheme={profileData?.lichessBoardTheme || 'brown'}
      />
    );
  };

  const renderGoals = () => (
    <div className="space-y-6">
      {profileData.chessStory && (
        <div>
          <h4 className="font-bold text-white mb-2">What's your chess story?</h4>
          <p className="text-gray-200 whitespace-pre-wrap">{profileData.chessStory}</p>
        </div>
      )}

      {profileData.loveAboutChess && (
        <div>
          <h4 className="font-bold text-white mb-2">What do you love most about chess?</h4>
          <p className="text-gray-200 whitespace-pre-wrap">{profileData.loveAboutChess}</p>
        </div>
      )}

      {profileData.favoriteOpening && (
        <div>
          <h4 className="font-bold text-white mb-2">What's your favorite opening?</h4>
          <p className="text-gray-200 whitespace-pre-wrap">{profileData.favoriteOpening}</p>
        </div>
      )}

      {profileData.funChessFact && (
        <div>
          <h4 className="font-bold text-white mb-2">Fun chess fact about you?</h4>
          <p className="text-gray-200 whitespace-pre-wrap">{profileData.funChessFact}</p>
        </div>
      )}

      {profileData.chessGoals && (
        <div>
          <h4 className="font-bold text-white mb-2">What are your chess goals?</h4>
          <p className="text-gray-200 whitespace-pre-wrap">{profileData.chessGoals}</p>
        </div>
      )}
    </div>
  );

  const renderChessGame = () => {
    if (!profileData.chessGame?.enabled) {
      return (
        <div className="text-center py-8">
          <i className="fas fa-chess-board text-4xl text-gray-400 mb-4"></i>
          <p className="text-gray-300">Chess game is not enabled.</p>
        </div>
      );
    }

    // Use profileData.id as fallback if profileId is undefined
    const actualProfileId = profileId || profileData.id;

    return (
      <LiveChessGame
        profileId={actualProfileId}
        profileOwnerId={profileData.ownerId || profileData.userId}
        chessGameConfig={profileData.chessGame}
        isOwnerView={!isPublicView}
        accentColor={primaryColor}
        profileUsername={profileData.username || profileData.slug || ''}
        profileDisplayName={profileData.displayName || ''}
        showTurnBanner={isUserTurn || showTransitionState}
        isTransitioning={showTransitionState}
        title={null}
      />
    );
  };

  const renderCustomSection = (sectionData) => {
    if (!sectionData) {
      return (
        <div className="text-center py-8">
          <i className="fas fa-puzzle-piece text-4xl text-gray-400 mb-4"></i>
          <p className="text-gray-300">Custom section is being configured.</p>
        </div>
      );
    }

    if (sectionData.type === 'link') {
      return (
        <div className="text-center py-8">
          <p className="text-gray-300 mb-4">This section links to an external page.</p>
          <a 
            href={formatUrl(sectionData.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors hover:opacity-95"
            style={{
              backgroundColor: primaryColor,
              boxShadow: `0 8px 20px ${hexToRgba(primaryColor, 0.3)}`
            }}
          >
            <i className="fas fa-external-link-alt mr-2"></i>
            Visit Link
          </a>
        </div>
      );
    }

    // Expandable content type
    return (
      <div className="space-y-4">
        {sectionData.content ? (
          <div>
            <div className="text-gray-200 whitespace-pre-wrap">
              {parseLinksFromText(sectionData.content, primaryColor)}
            </div>
            {sectionData.expandableLink && (
              <div className="pt-4">
                <a
                  href={sectionData.expandableLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center px-6 py-3 text-white rounded-lg transition-colors font-medium hover:opacity-95"
                  style={{
                    backgroundColor: primaryColor,
                    boxShadow: `0 8px 20px ${hexToRgba(primaryColor, 0.3)}`
                  }}
                >
                  <span>{sectionData.expandableLinkLabel || 'Learn More'}</span>
                  <i className="fas fa-external-link-alt ml-2"></i>
                </a>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-8">
            <i className="fas fa-puzzle-piece text-4xl text-gray-400 mb-4"></i>
            <p className="text-gray-300">No content added to this section yet.</p>
          </div>
        )}
      </div>
    );
  };

  const renderAffiliateLinks = () => {
    const affiliateLinks = profileData.affiliateLinks || [];
    const activeLinks = affiliateLinks.filter(link => link.isActive);

    if (activeLinks.length === 0) {
      return null;
    }

    const formatUrl = (url) => {
      if (!url) return url;
      if (!url.match(/^https?:\/\//)) {
        return `https://${url}`;
      }
      return url;
    };

    return (
      <div className="space-y-3">
        {activeLinks.map((link) => (
          <a
            key={link.id}
            href={formatUrl(link.url)}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full bg-gray-800 hover:bg-gray-700 rounded-lg p-4 shadow-md border-2 transition-all group"
            style={{ borderColor: primaryColor }}
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div
                  className="w-10 h-10 rounded-full flex items-center justify-center text-white"
                  style={{ backgroundColor: primaryColor }}
                >
                  <i className={`fas ${link.icon} text-lg`}></i>
                </div>
                <div>
                  <h4 className="font-semibold text-white group-hover:opacity-90 transition">
                    {link.title}
                  </h4>
                  {link.description && (
                    <p className="text-sm text-gray-400">{link.description}</p>
                  )}
                </div>
              </div>
              <i className="fas fa-external-link-alt text-gray-400 group-hover:text-white transition"></i>
            </div>
          </a>
        ))}
      </div>
    );
  };

  const renderReferral = () => {
    const referralConfig = profileData.referral || {};
    const origin = typeof window !== 'undefined' ? window.location.origin : 'https://chessweb.site';
    const referralUrl = `${origin}/me?ref=${profileData.userId || profileData.id}`;

    return (
      <div className="text-center py-8 space-y-6">
        {/* Icon */}
        <div className="mb-4">
          <i className="fas fa-rocket text-5xl md:text-6xl" style={{ color: primaryColor }}></i>
        </div>

        {/* Description */}
        <h4 className="text-lg md:text-xl font-semibold text-gray-100 px-4">
          {referralConfig.description || 'Build your own professional chess profile in minutes - completely free!'}
        </h4>

        {/* CTA Button - Full width on mobile */}
        <a
          href={referralUrl}
          className="inline-flex items-center justify-center w-full sm:w-auto px-8 py-4 text-white rounded-lg text-lg font-semibold transition-all hover:opacity-90 shadow-lg"
          style={{
            backgroundColor: referralConfig.buttonColor || primaryColor,
            boxShadow: `0 8px 20px ${hexToRgba(referralConfig.buttonColor || primaryColor, 0.3)}`
          }}
        >
          <i className="fas fa-plus-circle mr-2"></i>
          Get Started Free
        </a>

        {/* Subtext */}
        <p className="text-sm text-gray-300 px-4">
          Join {profileData.displayName || 'me'} and create your chess profile
        </p>
      </div>
    );
  };

  const renderSectionContent = (sectionId) => {
    switch (sectionId) {
      case 'basicInfo':
        return renderBasicInfo();
      case 'chessDetails':
        return renderChessDetails();
      case 'achievements':
        return renderAchievements();
      case 'coaching':
        return renderCoaching();
      case 'photos':
        return renderPhotos();
      case 'youtube':
        return renderYouTube();
      case 'lichessStudies':
        return renderLichessStudies();
      case 'goals':
        return renderGoals();
      case 'chessGame':
        return renderChessGame();
      case 'affiliateLinks':
        return renderAffiliateLinks();
      case 'referral':
        return renderReferral();
      case 'blog':
        return <BlogSection profileId={profileId} username={profileData?.username} accentColor={primaryColor} />;
      case 'shop':
        return (
          <ShopPreviewSection
            profileId={profileId}
            vendorId={profileData?.userId}
            accentColor={primaryColor}
          />
        );
      case 'contact':
        return (
          <ContactFormSection
            profileId={profileId}
            username={profileData?.username}
            isPremium={profileData?.playerPremium}
            accentColor={primaryColor}
            description={profileData?.contactDescription}
          />
        );
      default:
        // Handle custom sections
        if (sectionId.startsWith('custom_')) {
          const sectionKey = sectionId.replace('custom_', '');
          const sectionData = customSections[sectionKey];
          return renderCustomSection(sectionData);
        }
        return null;
    }
  };

  if (visibleSections.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow p-8 text-center">
        <p className="text-gray-600">No sections available for this profile.</p>
      </div>
    );
  }

  return (
    <div className="py-8 overflow-x-hidden">
      <div className="max-w-4xl mx-auto px-4">
        {/* Profile Header */}
        <div className="text-center mb-8">
          {/* Profile Photo */}
          <div className="mb-6">
            {profileData.photoUrl ? (
              <img
                src={profileData.photoUrl}
                alt={profileData.displayName}
                className="w-32 h-32 rounded-full object-cover mx-auto shadow-lg"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-32 h-32 rounded-full bg-gray-300 mx-auto shadow-lg flex items-center justify-center">
                <i className="fas fa-user text-gray-500 text-4xl"></i>
              </div>
            )}
          </div>

          {/* Name with Copy Link */}
          <button
            onClick={handleCopyLink}
            className="text-4xl font-bold text-white mb-2 hover:opacity-90 transition cursor-pointer flex items-center justify-center gap-2 text-center mx-auto"
            title="Copy profile link"
            style={{
              textShadow: `0 0 25px ${hexToRgba(primaryColor, 0.6)}`
            }}
          >

            {profileData.playerPremium ? (
              <i className="fas fa-crown text-lg" style={{ color: primaryColor }}></i>
            ) : (
              <i className="fas fa-link text-lg" style={{ color: primaryColor }}></i>
            )}

            {(profileData.titleCEO || profileData.fideTitle) && (
              <span
                className={`${
                  profileData.titleCEO || profileData.fideTitle === 'CEO'
                    ? 'bg-yellow-500 text-black'
                    : profileData.username === 'charlize'
                      ? 'text-white hover:opacity-90'
                      : 'bg-red-600 text-white hover:bg-red-700'
                } px-2 py-1 rounded text-sm font-bold mr-1 cursor-pointer transition-colors`}
                style={
                  profileData.titleCEO || profileData.fideTitle === 'CEO'
                    ? {backgroundColor: '#ffd700'}
                    : profileData.username === 'charlize'
                      ? {backgroundColor: '#f980a7'}
                      : {}
                }
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedTitle(!expandedTitle);
                }}
                title={profileData.titleCEO ? 'CEO of Chess Website' : (titleFullNames[profileData.fideTitle] || profileData.fideTitle)}
              >
                {profileData.titleCEO ? (expandedTitle ? 'CEO of Chess Website' : 'CEO') : (expandedTitle ? titleFullNames[profileData.fideTitle] || profileData.fideTitle : profileData.fideTitle)}
              </span>
            )}

            {/* Profile Type Badge */}
            {profileType && profileTypeConfig[profileType] && (
              <span
                className="bg-red-600 text-white hover:bg-red-700 px-2 py-1 rounded text-sm font-bold mr-1 transition-colors flex items-center gap-1"
                title={profileTypeConfig[profileType].label}
              >
                <i className={`fas ${profileTypeConfig[profileType].icon} text-xs`}></i>
                <span>{profileTypeConfig[profileType].label}</span>
              </span>
            )}
            
            {profileData.displayName || 'Chess Player'}

          
          </button>
          
          {/* Copy Feedback */}
          {copyFeedback && (
            <div className="text-sm text-red-600 animate-pulse mb-2">
              {copyFeedback}
            </div>
          )}
        </div>

        {/* Social Links Bubbles */}
        <SocialLinksBubbles
          socialLinks={profileData.socialLinks || {}}
          chessLinks={profileData.links || {}}
          primarySocialProfile={profileData.primarySocialProfile}
          onChessPlatformClick={handleChessPlatformClick}
          formData={profileData}
        />

        {/* Subscribe Button - Show if blog is active and has blogs */}
        {enhancedToggles.showBlog && hasBlogsToSubscribe && (
          <>
            <style>
              {`
                @keyframes breathe {
                  0%, 100% {
                    transform: scale(1);
                    box-shadow: 0 0 0 0 #9333ea40;
                  }
                  50% {
                    transform: scale(1.02);
                    box-shadow: 0 0 20px 8px #9333ea60;
                  }
                }
              `}
            </style>
            <div className="flex justify-center mt-4 mb-4">
              <div
                style={{
                  animation: showSubscribeButtonAnimation ? 'breathe 1.5s ease-in-out' : undefined
                }}
              >
                <button
                  onClick={handleScrollToSubscribe}
                  className="px-4 py-2 text-white rounded text-sm font-bold transition-all shadow-md hover:shadow-lg hover:opacity-90 flex items-center gap-2"
                  style={{
                    background: 'linear-gradient(to bottom, #9333ea 0%, #4f46e5 100%)'
                  }}
                >
                 Subscribe <i className="fas fa-envelope"></i>

                </button>
              </div>
            </div>
          </>
        )}

        {/* Chess Platform Bubble Tab */}
        {activeChessPlatform && (
          <ChessPlatformBubbleTab
            platform={activeChessPlatform}
            username={profileData.links?.[activeChessPlatform]}
            onClose={() => setActiveChessPlatform(null)}
            accentColor={primaryColor}
          />
        )}

        {/* Chess Game Section - At top when urgent or transitioning */}
        {(isUserTurn || showTransitionState) && enhancedToggles.showChessGame && profileData.chessGame?.enabled && (
          <div className="mt-12 mb-12">
            <div className="w-full max-w-2xl mx-auto">
              <div
                className="border-2 rounded-lg transition-all duration-500 shadow-lg bg-gray-900"
                style={{
                  borderColor: primaryColor,
                  boxShadow: isUserTurn
                    ? `0 18px 36px ${hexToRgba(primaryColor, 0.35)}`
                    : `0 12px 28px ${hexToRgba(primaryColor, 0.22)}`
                }}
              >
                {/* Header */}
                <button
                  onClick={() => handleBlockToggle('chessGame')}
                  className={`w-full p-4 transition-all duration-500 border ${
                    isChessGameExpanded
                      ? 'rounded-t-lg border-b-2'
                      : 'rounded-lg'
                  } ${
                    isChessGameExpanded
                      ? ''
                      : 'hover:bg-gray-800'
                  }`}
                  style={{
                    ...chessGameHeaderStyle,
                    borderColor: hexToRgba(primaryColor, 0.4)
                  }}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center transition-colors duration-500"
                        style={chessGameIconWrapperStyle}
                      >
                        <i
                          className="fas fa-chess-board text-lg transition-colors duration-500"
                          style={chessGameIconStyle}
                        ></i>
                      </div>
                      <h3
                        className="text-lg font-bold transition-colors duration-500"
                        style={chessGameTitleStyle}
                      >
                        {profileData.chessGame?.title || 'Play Chess'}
                      </h3>
                    </div>
                    <div className="flex items-center space-x-2">
                      {isUserTurn ? (
                        <span
                          className="px-3 py-1 rounded-full text-sm font-semibold"
                          style={{
                            backgroundColor: '#ffffff',
                            color: primaryColor,
                            border: `1px solid ${hexToRgba(primaryColor, 0.35)}`
                          }}
                        >
                          🚨 My Move
                        </span>
                      ) : showTransitionState ? (
                        <span
                          className="px-3 py-1 rounded-full text-sm font-semibold animate-pulse"
                          style={{
                            backgroundColor: hexToRgba(primaryColor, 0.2),
                            color: primaryColor
                          }}
                        >
                          ✅ Move Sent
                        </span>
                      ) : null}
                      <i
                        className={`fas fa-chevron-${isChessGameExpanded ? 'up' : 'down'} transition-colors duration-500`}
                        style={chessGameChevronStyle}
                      ></i>
                    </div>
                  </div>
                </button>

                {/* Chess Game Content */}
                {expandedBlock === 'chessGame' && (
                  <div className="p-6 bg-transparent rounded-b-2xl">
                    {renderChessGame()}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* LinkBlocks for each section */}
        <div className="space-y-4">
          {visibleSections.map(section => {
            // Skip chess game only when it's shown at the top
            if (section.id === 'chessGame' && (isUserTurn || showTransitionState) && enhancedToggles.showChessGame && profileData.chessGame?.enabled) {
              return null;
            }

            // Handle blog section - render directly without LinkBlock wrapper
            if (section.id === 'blog') {
              return (
                <div
                  key={section.id}
                  ref={blogSubscribeRef}
                  className={section.id === topSectionId ? 'mt-12 mb-12' : ''}
                  style={section.id === topSectionId ? {
                    filter: `drop-shadow(0 0 35px ${hexToRgba(primaryColor, 0.7)})`
                  } : {}}
                >
                  <BlogSectionTitle username={profileData?.displayName} accentColor={primaryColor} />
                  <BlogSection
                    profileId={profileId}
                    username={profileData?.username}
                    accentColor={primaryColor}
                    onBlogsLoaded={setHasBlogsToSubscribe}
                    animate={justClickedSubscribe}
                    isOwner={isOwner}
                  />
                </div>
              );
            }

            // Handle shop section - render directly without LinkBlock wrapper
            if (section.id === 'shop') {
              return (
                <div
                  key={section.id}
                  className={section.id === topSectionId ? 'mt-12 mb-12' : ''}
                  style={section.id === topSectionId ? {
                    filter: `drop-shadow(0 0 35px ${hexToRgba(primaryColor, 0.7)})`
                  } : {}}
                >
                  <ShopPreviewSection
                    profileId={profileId}
                    vendorId={profileData?.userId}
                    accentColor={primaryColor}
                  />
                </div>
              );
            }

            // Handle photo gallery section - render directly without LinkBlock wrapper
            if (section.id === 'photos') {
              return (
                <div
                  key={section.id}
                  className={section.id === topSectionId ? 'mt-12 mb-12' : ''}
                  style={section.id === topSectionId ? {
                    filter: `drop-shadow(0 0 35px ${hexToRgba(primaryColor, 0.7)})`
                  } : {}}
                >
                  <PhotoGallerySection profileData={profileData} accentColor={primaryColor} />
                </div>
              );
            }

            // Handle YouTube gallery section - render directly without LinkBlock wrapper
            if (section.id === 'youtube') {
              return (
                <div
                  key={section.id}
                  className={section.id === topSectionId ? 'mt-12 mb-12' : ''}
                  style={section.id === topSectionId ? {
                    filter: `drop-shadow(0 0 35px ${hexToRgba(primaryColor, 0.7)})`
                  } : {}}
                >
                  <YouTubeGallerySection profileData={profileData} accentColor={primaryColor} />
                </div>
              );
            }

            // Handle Lichess studies section - render directly without LinkBlock wrapper
            if (section.id === 'lichessStudies') {
              return (
                <div
                  key={section.id}
                  className={section.id === topSectionId ? 'mt-12 mb-12' : ''}
                  style={section.id === topSectionId ? {
                    filter: `drop-shadow(0 0 35px ${hexToRgba(primaryColor, 0.7)})`
                  } : {}}
                >
                  <LichessStudiesSection profileData={profileData} accentColor={primaryColor} />
                </div>
              );
            }

            // Handle referral section as a simple clickable link
            if (section.id === 'referral') {
              const referralConfig = profileData.referral || {};
              const origin = typeof window !== 'undefined' ? window.location.origin : 'https://chessweb.site';
              const referralUrl = `${origin}/me?ref=${profileData.userId || profileData.id}`;

              return (
                <LinkBlock
                  key={section.id}
                  icon={section.icon}
                  title={section.label}
                  isExpanded={false}
                  onClick={() => window.location.href = referralUrl}
                  isExternalLink={true}
                  accentColor={primaryColor}
                  isTopSection={section.id === topSectionId}
                >
                  <div className="text-center py-4">
                    <p className="text-gray-300 mb-4">
                      {referralConfig.description || 'Build your own professional chess profile for free!'}
                    </p>
                  </div>
                </LinkBlock>
              );
            }

            // Handle custom sections with link type differently
            if (section.isCustom && section.data?.type === 'link' && section.data?.url) {
              return (
                <LinkBlock
                  key={section.id}
                  icon={section.icon}
                  title={section.label}
                  isExpanded={false}
                  onClick={() => window.open(formatUrl(section.data.url), '_blank')}
                  isExternalLink={true}
                  isCustom={section.isCustom}
                  accentColor={primaryColor}
                  isTopSection={section.id === topSectionId}
                >
                  <div className="text-center py-4">
                    <p className="text-gray-300 mb-4">Click to visit external link</p>
                    <div className="text-sm break-all" style={{ color: primaryColor }}>{section.data.url}</div>
                  </div>
                </LinkBlock>
              );
            }

            // Standard expandable sections
            return (
              <div
                key={section.id}
                ref={(el) => {
                  sectionRefs.current[section.id] = el;
                  if (section.id === 'chessGame') {
                    chessGameRef.current = el;
                  }
                }}
              >
                <LinkBlock
                  icon={section.icon}
                  title={section.label}
                  isExpanded={expandedBlock === section.id}
                  onClick={() => handleBlockToggle(section.id)}
                  isCustom={section.isCustom}
                  hideTitle={false}
                  accentColor={primaryColor}
                  isTopSection={section.id === topSectionId}
                >
                  {renderSectionContent(section.id)}
                </LinkBlock>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default PlayerPreviewSection;
